import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { arrayMove } from '@dnd-kit/sortable';
import { Box, Button, Stack } from '@mui/material';
import moment from 'moment';

import { CfsApi, UnitApi } from '@/api';
import { LocalStorageItems } from '@/constants';
import { withLoader, WithLoaderProps } from '@/hocs';
import { PageBackHeader, TabAnimationWrapper } from '@/layouts/TabLayout';
import {
  CFS_TIMELINE_EVENTS,
  CFSEvent,
  IAdditionalInfo,
  ICFSLog,
  IUnitShift,
  IUser,
  PINNED_LIVE_INFO_EVENT,
} from '@/models';
import { CFSTimelineStepper } from '@/pages/CFS/CFSTimeline/components/CFSTimelineStepper';
import { TimelineFilterMenu } from '@/pages/CFS/CFSTimeline/TimelineFilterMenu';
import { isCFSViewOnly } from '@/services';
import { useToastStore, useUserStore } from '@/store';
import { reconcileStarEditDuplicates } from '@/utils';

import {
  getEffectiveTimestamp,
  orderLogsForDisplay,
  TimelineLogItem,
} from './utils/timelineUtils';

const CFSTimeline = ({ showLoader, hideLoader }: WithLoaderProps) => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<ICFSLog[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draftLogs, setDraftLogs] = useState<TimelineLogItem[]>([]);
  const [initialNaIds, setInitialNaIds] = useState<Set<string>>(new Set());
  const [baseOrderIndex, setBaseOrderIndex] = useState<Record<string, number>>(
    {},
  );
  const [naGroupOverrides, setNaGroupOverrides] = useState<
    Record<string, number>
  >({});
  const descriptionDraftsRef = useRef<Record<string, string>>({});
  const [cfsAdditionalInfo, setCfsAdditionalInfo] = useState<IAdditionalInfo[]>(
    [],
  );
  const [cfsCreatedAt, setCfsCreatedAt] = useState<number | null>(null);
  const [cfsReporterTime, setCfsReporterTime] = useState<number | null>(null);
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);

  const fetchCfsDetails = async () => {
    try {
      // cfs additional info is gotten from main cfs response object as a field, no endpoint for it
      const cfs = await CfsApi.getOne(cfsId);
      setCfsAdditionalInfo(cfs.data.additionalInfo);
      if (cfs.data.createdAt) {
        setCfsCreatedAt(new Date(cfs.data.createdAt).getTime());
      }
      if (cfs.data.reporter?.reportTime) {
        setCfsReporterTime(new Date(cfs.data.reporter.reportTime).getTime());
      }
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  // Reconcile duplicates from star/unstar edits, then filter to retrieve
  // only cfsAdditionalInfo that starts with asterisk (*)
  const cfsAdditionalInfoWithAsterisk = useMemo(() => {
    if (
      selectedEvents.length > 0 &&
      !selectedEvents.includes(PINNED_LIVE_INFO_EVENT)
    ) {
      return [];
    }
    const reconciled = reconcileStarEditDuplicates(cfsAdditionalInfo);
    return reconciled.filter(
      (info: any) => info.description.startsWith('*') && !info.isDeleted,
    );
  }, [cfsAdditionalInfo, selectedEvents]);

  const [historyUnitShifts, setHistoryUnitShifts] = useState<{
    [key: string]: IUnitShift;
  }>({});

  const { updateToast } = useToastStore();
  const { user } = useUserStore();
  const urlParams = useParams();
  const cfsId = String(urlParams.cfsId);

  const extractUnitShifts = (localLogs: ICFSLog[]): string[] => {
    return localLogs.reduce((acc: string[], log) => {
      const { data } = log || {};
      const { new: newData } = data || {};

      if (newData?.assignedUnitShifts?.unitShift) {
        const unitShift = newData.assignedUnitShifts.unitShift as string;
        if (!acc.includes(unitShift)) {
          acc.push(unitShift);
        }
      }

      return acc;
    }, []);
  };

  const buildSyntheticLog = (event: CFSEvent): TimelineLogItem => {
    const createdAt =
      cfsCreatedAt !== null ? new Date(cfsCreatedAt) : new Date();
    return {
      _id: `synthetic:${event}`,
      app: 'account',
      cfs: cfsId,
      database: '',
      event,
      field: '',
      data: {},
      logCollection: '',
      collection: '',
      createdBy: user || ({} as IUser),
      account: '',
      createdAt,
      updatedAt: createdAt,
      overriddenAt: null,
      groupDay: null,
      sortOrder: undefined,
      isSynthetic: true,
    };
  };

  const buildTimelineLogs = (): TimelineLogItem[] => {
    const normalizedLogs = logs.map((log) => ({ ...log }));
    const callEvents = [CFSEvent.CALL_RECEIVED, CFSEvent.CALL_ANSWERED];
    const existingEvents = new Set(normalizedLogs.map((log) => log.event));
    const shouldIncludeEvent = (event: CFSEvent) =>
      selectedEvents.length === 0 || selectedEvents.includes(event);
    const syntheticLogs = callEvents
      .filter(
        (event) => !existingEvents.has(event) && shouldIncludeEvent(event),
      )
      .map(buildSyntheticLog);

    // Add synthetic CREATED log if none exists, using reporter.reportTime
    if (
      !existingEvents.has(CFSEvent.CREATED) &&
      shouldIncludeEvent(CFSEvent.CREATED)
    ) {
      const syntheticCreated = buildSyntheticLog(CFSEvent.CREATED);
      if (cfsReporterTime !== null) {
        syntheticCreated.overriddenAt = cfsReporterTime;
      }
      syntheticLogs.push(syntheticCreated);
    }

    return [...normalizedLogs, ...syntheticLogs];
  };

  const isNaTime = (log: TimelineLogItem) => log.overriddenAt === null;

  const fetchLogsWithFilter = async (eventFilter?: string[]) => {
    if (!cfsId) return;

    try {
      showLoader();

      let filterEvents =
        eventFilter && eventFilter.length > 0
          ? [...eventFilter]
          : CFS_TIMELINE_EVENTS;
      // remove 'pinned live info' event from the event filter
      filterEvents = filterEvents.filter(
        (event) => event !== PINNED_LIVE_INFO_EVENT,
      );
      if (filterEvents.length === 0) {
        setLogs([]);
        setHasLoaded(true);
        return;
      }

      const params = {
        sort: JSON.stringify({ createdAt: -1 }),
        limit: 1000,
        filter: JSON.stringify({
          event: {
            $in: filterEvents.length > 0 ? filterEvents : CFS_TIMELINE_EVENTS,
          },
        }),
      };

      const res = await CfsApi.getCfsHistory(cfsId, params);
      const fetchedLogs = res.data.results;

      setLogs(fetchedLogs);
      setHasLoaded(true);

      const unitShifts = extractUnitShifts(fetchedLogs);
      if (unitShifts.length > 0) {
        await fetchUnitShifts(unitShifts);
      }

      return fetchedLogs;
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
      setHasLoaded(true);
      return null;
    } finally {
      hideLoader();
    }
  };

  const timelineLogs = useMemo(() => {
    return orderLogsForDisplay(buildTimelineLogs());
  }, [logs, cfsCreatedAt, cfsReporterTime, user, selectedEvents]);

  const handleFilterChange = async (events: string[]) => {
    await fetchLogsWithFilter(events);
    setSelectedEvents(events);
  };

  const handleEnterEditMode = () => {
    if (selectedEvents.length > 0) {
      updateToast({
        open: true,
        message: 'Clear filters before editing the timeline.',
      });
      return;
    }
    const draft = timelineLogs.map((log) => ({
      ...log,
      data: log.data ? { ...log.data } : log.data,
    }));
    setDraftLogs(draft);
    setInitialNaIds(
      new Set(
        draft
          .filter((log) => isNaTime(log) && !log.isSynthetic && !log.isNew)
          .map((log) => (log._id ? String(log._id) : '')),
      ),
    );
    setBaseOrderIndex(
      draft.reduce((acc, log, index) => {
        if (log._id) acc[String(log._id)] = index;
        return acc;
      }, {} as Record<string, number>),
    );
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setDraftLogs([]);
    setInitialNaIds(new Set());
    setBaseOrderIndex({});
    descriptionDraftsRef.current = {};
  };

  const handleTimeChange = (logId: string, value: number | null) => {
    const normalizedValue = value ?? null;
    setDraftLogs((prev) =>
      prev.map((log) =>
        String(log._id) === logId
          ? { ...log, overriddenAt: normalizedValue }
          : log,
      ),
    );
  };

  const handleClearTime = (logId: string) => {
    setDraftLogs((prev) =>
      prev.map((log) =>
        String(log._id) === logId ? { ...log, overriddenAt: null } : log,
      ),
    );
  };

  const handleDescriptionChange = (logId: string, value: string) => {
    descriptionDraftsRef.current[logId] = value;
  };

  const handleAddCustomEvent = () => {
    const activeLogs = draftLogs.filter((log) => !log.isDeleted);
    const latestDayStart =
      activeLogs.length > 0
        ? Math.max(
            ...activeLogs.map((log) =>
              moment(log.createdAt).startOf('day').valueOf(),
            ),
          )
        : moment(cfsCreatedAt || Date.now())
            .startOf('day')
            .valueOf();
    const latestDayMaxCreatedAt = activeLogs
      .filter(
        (log) =>
          moment(log.createdAt).startOf('day').valueOf() === latestDayStart,
      )
      .reduce((max, log) => {
        const ts = new Date(log.createdAt).getTime();
        return ts > max ? ts : max;
      }, latestDayStart);
    const createdAt = new Date(latestDayMaxCreatedAt);
    const newLog: TimelineLogItem = {
      _id: `custom:${Date.now()}`,
      app: 'account',
      cfs: cfsId,
      database: '',
      event: CFSEvent.CUSTOM_EVENT,
      field: '',
      data: { new: { description: '' } },
      logCollection: '',
      collection: '',
      createdBy: user || ({} as IUser),
      account: '',
      createdAt,
      updatedAt: createdAt,
      overriddenAt: null,
      groupDay: null,
      sortOrder: undefined,
      isNew: true,
    };
    setDraftLogs((prev) => [...prev, newLog]);
  };

  const handleDeleteCustomEvent = (logId: string) => {
    setDraftLogs((prev) => {
      const next: TimelineLogItem[] = [];
      for (const log of prev) {
        if (String(log._id) !== logId) {
          next.push(log);
          continue;
        }
        if (log.isNew) {
          continue;
        }
        next.push({ ...log, isDeleted: true });
      }
      return next;
    });
  };

  const handleReorderLogs = (activeId: string, overId: string) => {
    // View-only users cannot reorder. Guard here because the not-editing branch
    // below persists via CfsApi.updateTimeline even when not in edit mode.
    if (isCFSViewOnly(user as IUser)) return;
    if (activeId === overId) return;
    if (!isEditing) {
      const orderedLogs = timelineLogs;
      const oldIndex = orderedLogs.findIndex(
        (log) => String(log._id) === activeId,
      );
      const newIndex = orderedLogs.findIndex(
        (log) => String(log._id) === overId,
      );
      if (oldIndex === -1 || newIndex === -1) return;
      const activeLog = orderedLogs[oldIndex];
      const overLog = orderedLogs[newIndex];
      const reorderedLogs = arrayMove(orderedLogs, oldIndex, newIndex);
      const sortMap = reorderedLogs.reduce<Record<string, number>>(
        (acc, log, idx) => {
          if (log._id) {
            acc[String(log._id)] = idx + 1;
          }
          return acc;
        },
        {},
      );
      const getGroupDay = (entry: TimelineLogItem) => {
        const entryId = entry._id ? String(entry._id) : '';
        const overrideDay =
          entry.overriddenAt === null && entryId
            ? naGroupOverrides[entryId] ?? entry.groupDay
            : undefined;
        const effectiveTimestamp =
          typeof overrideDay === 'number'
            ? overrideDay
            : typeof entry.overriddenAt === 'number'
            ? entry.overriddenAt
            : new Date(entry.createdAt).getTime();
        return moment(effectiveTimestamp).startOf('day').valueOf();
      };
      const nextLogs = logs.map((log) => {
        const id = log._id ? String(log._id) : '';
        if (!id) return log;
        const nextSortOrder = sortMap[id];
        const nextGroupDay =
          activeLog?.overriddenAt === null &&
          String(activeLog?._id) === id &&
          overLog?._id
            ? getGroupDay(overLog)
            : log.groupDay ?? null;
        return {
          ...log,
          sortOrder: nextSortOrder ?? log.sortOrder,
          groupDay: nextGroupDay,
        };
      });
      if (activeLog?.overriddenAt === null && activeLog?._id && overLog?._id) {
        const targetDay =
          nextLogs.find((log) => String(log._id) === String(activeLog._id))
            ?.groupDay ?? null;
        setNaGroupOverrides((prev) => ({
          ...prev,
          [String(activeLog._id)]: targetDay ?? prev[String(activeLog._id)],
        }));
      }
      setLogs(nextLogs);
      const payloadLogs = reorderedLogs
        .map((log, index) => {
          // Only persist synthetic events whose time was actually set. An
          // untimed synthetic must not be inserted (or blank an existing
          // log's time) on a mere reorder.
          const shouldCreateSynthetic =
            log.isSynthetic &&
            typeof log.overriddenAt === 'number' &&
            !Number.isNaN(log.overriddenAt);
          if (log.isSynthetic && !shouldCreateSynthetic) {
            return null;
          }
          const basePayload: any = {
            event: log.event,
            sortOrder: index + 1,
          };
          if (!log.isSynthetic) {
            basePayload._id = log._id;
          }
          if (log.overriddenAt !== undefined) {
            basePayload.overriddenAt = log.overriddenAt;
          }
          if (log.overriddenAt === null) {
            if (log.isSynthetic) {
              basePayload.groupDay = getGroupDay(log);
            } else {
              basePayload.groupDay = log.groupDay ?? null;
            }
          } else {
            basePayload.groupDay = null;
          }
          return basePayload;
        })
        .filter(Boolean);
      showLoader();
      CfsApi.updateTimeline(
        cfsId,
        {
          logs: payloadLogs,
        },
        {
          eventType: CFS_TIMELINE_EVENTS.filter(
            (event) => event !== PINNED_LIVE_INFO_EVENT,
          ).join(','),
        },
      )
        .then((res) => {
          setLogs(res.data);
        })
        .catch((err: any) => {
          updateToast({ open: true, message: err.message });
          setLogs(logs);
        })
        .finally(() => {
          hideLoader();
        });
      return;
    }
    setDraftLogs((prev) => {
      const visibleLogs = prev.filter((log) => !log.isDeleted);
      const oldIndex = visibleLogs.findIndex(
        (log) => String(log._id) === activeId,
      );
      const newIndex = visibleLogs.findIndex(
        (log) => String(log._id) === overId,
      );
      if (oldIndex === -1 || newIndex === -1) return prev;
      const reordered = arrayMove(visibleLogs, oldIndex, newIndex);
      const deletedLogs = prev.filter((log) => log.isDeleted);
      return [...reordered, ...deletedLogs];
    });
  };

  const handleSaveEdit = async () => {
    const activeLogs = draftLogs.filter((log) => !log.isDeleted);

    for (const log of activeLogs) {
      if (log.event === CFSEvent.CUSTOM_EVENT) {
        const description =
          descriptionDraftsRef.current[String(log._id)] ??
          log.data?.new?.description ??
          '';
        if (!description.trim()) {
          updateToast({
            open: true,
            message: 'Custom event description is required.',
          });
          return;
        }
      }
    }

    const naIndexes = activeLogs
      .map((log, index) => (isNaTime(log) ? index : null))
      .filter((index): index is number => index !== null);
    const naIndexSet = new Set(naIndexes);
    const naLogs = activeLogs.filter(isNaTime);
    const timedLogs = activeLogs.filter((log) => !isNaTime(log));

    const timedSorted = timedLogs
      .map((log) => ({
        log,
        timestamp: getEffectiveTimestamp(log) ?? 0,
      }))
      .sort((a, b) => {
        if (a.timestamp !== b.timestamp) {
          return a.timestamp - b.timestamp;
        }
        const aIndex = a.log._id ? baseOrderIndex[String(a.log._id)] ?? 0 : 0;
        const bIndex = b.log._id ? baseOrderIndex[String(b.log._id)] ?? 0 : 0;
        return aIndex - bIndex;
      })
      .map(({ log }) => log);

    const finalOrder: TimelineLogItem[] = [];
    let naCursor = 0;
    let timeCursor = 0;
    for (let index = 0; index < activeLogs.length; index += 1) {
      if (naIndexSet.has(index)) {
        finalOrder.push(naLogs[naCursor]);
        naCursor += 1;
      } else {
        finalOrder.push(timedSorted[timeCursor]);
        timeCursor += 1;
      }
    }

    const payloadLogs = finalOrder
      .map((log, index) => {
        const shouldCreateSynthetic =
          log.isSynthetic &&
          typeof log.overriddenAt === 'number' &&
          !Number.isNaN(log.overriddenAt);
        if (log.isSynthetic && !shouldCreateSynthetic) {
          return null;
        }

        const payload: any = {
          event: log.event,
          sortOrder: index + 1,
        };

        if (!log.isSynthetic && !log.isNew) {
          payload._id = log._id;
        }

        if (log.overriddenAt !== undefined) {
          payload.overriddenAt = log.overriddenAt;
        }
        payload.groupDay =
          log.overriddenAt === null ? log.groupDay ?? null : null;

        if (log.event === CFSEvent.CUSTOM_EVENT) {
          const draftDescription =
            descriptionDraftsRef.current[String(log._id)] ??
            log.data?.new?.description ??
            '';
          payload.data = {
            new: {
              description: draftDescription.trim(),
            },
          };
        }

        return payload;
      })
      .filter(Boolean);

    const deletedPayload = draftLogs
      .filter((log) => log.isDeleted && log._id)
      .map((log) => ({
        _id: log._id,
        event: log.event,
        sortOrder: log.sortOrder ?? 0,
        deleted: true,
      }));

    try {
      showLoader();
      const eventType = CFS_TIMELINE_EVENTS.filter(
        (event) => event !== PINNED_LIVE_INFO_EVENT,
      ).join(',');
      const res = await CfsApi.updateTimeline(
        cfsId,
        {
          logs: [...payloadLogs, ...deletedPayload],
        },
        {
          eventType,
        },
      );
      setLogs(res.data);
      setIsEditing(false);
      setDraftLogs([]);
      setInitialNaIds(new Set());
      setBaseOrderIndex({});
      descriptionDraftsRef.current = {};
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    } finally {
      hideLoader();
    }
  };

  const fetchUnitShifts = async (unitShifts: string[]) => {
    try {
      showLoader();
      const params = {
        filter: JSON.stringify({
          _id: [...unitShifts],
        }),
      };
      const res = await UnitApi.getUnitShifts(params);
      setHistoryUnitShifts(
        res.data.results.reduce((accumulatedUnitShift, currentUnitShift) => {
          return currentUnitShift._id
            ? {
                ...accumulatedUnitShift,
                [currentUnitShift._id]: currentUnitShift,
              }
            : accumulatedUnitShift;
        }, {}),
      );
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    if (!user?._id) return;

    const savedKey = LocalStorageItems.TimelineFilterEvents;
    const savedFilter = localStorage.getItem(savedKey);
    const parsedFilter = savedFilter ? JSON.parse(savedFilter) : [];

    const loadTimeline = async () => {
      await fetchCfsDetails();
      await fetchLogsWithFilter(parsedFilter);
    };

    loadTimeline();
  }, [user?._id, cfsId]);

  const displayLogs = useMemo(() => {
    if (!isEditing) {
      return timelineLogs;
    }
    return draftLogs.filter((log) => !log.isDeleted);
  }, [isEditing, timelineLogs, draftLogs]);

  return (
    <TabAnimationWrapper>
      <Stack direction="row" sx={{ width: '100%' }} alignItems="center" gap={2}>
        <PageBackHeader
          title="Timeline"
          style={{ fontWeight: 500 }}
          goBack={() => navigate(-1)}
          backButtonSx={{ color: 'error.main' }}
        />
        <TimelineFilterMenu onChangeFilter={handleFilterChange} />
        <Box sx={{ flexGrow: 1 }} />
        {!isEditing ? (
          !isCFSViewOnly(user as IUser) && (
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleEnterEditMode}
            >
              Edit
            </Button>
          )
        ) : (
          <Stack direction="row" gap={1}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleCancelEdit}
            >
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={handleSaveEdit}>
              Save
            </Button>
          </Stack>
        )}
      </Stack>
      <Box mt={2}></Box>
      {(displayLogs.length > 0 || cfsAdditionalInfoWithAsterisk.length > 0) && (
        <CFSTimelineStepper
          historyUnitShifts={historyUnitShifts || {}}
          logs={displayLogs || []}
          cfsAdditionalInfo={cfsAdditionalInfoWithAsterisk || []}
          selectedEvents={selectedEvents || []}
          isEditing={isEditing}
          draggableNaIds={initialNaIds}
          onTimeChange={handleTimeChange}
          onClearTime={handleClearTime}
          onDescriptionChange={handleDescriptionChange}
          getDescriptionDraftValue={(logId, fallback) =>
            descriptionDraftsRef.current[logId] ?? fallback
          }
          onAddCustomEvent={handleAddCustomEvent}
          onDeleteCustomEvent={handleDeleteCustomEvent}
          onReorder={handleReorderLogs}
          currentUserId={user?._id}
          localNaGroupOverrides={naGroupOverrides}
        />
      )}
      {displayLogs.length === 0 &&
        cfsAdditionalInfoWithAsterisk.length === 0 &&
        hasLoaded && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            No timeline events match the selected filters.
          </Box>
        )}
    </TabAnimationWrapper>
  );
};

export default withLoader(CFSTimeline);
